import React, { useMemo } from 'react';
import _ from 'lodash';
import { Theme } from '@mui/material/styles';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Fields } from './Fields';
import { vendorsLogoByType } from '../../../../../constants';
import { ProductPrice } from '../../../cardPriceBlock/ProductPrice';
import { ProposalCardContainer } from '../cardContainer/ProposalCardContainer';
import {
  AftermarketProductTypes,
  MarkupType,
  ProposalProductCardByVendorProps,
  ProposalProductCardModes,
} from '../../../../../types';
import { TruckSuiteLossCode, TruckSuiteRate, TruckSuiteRateTerm } from './types';

const useStyles = makeStyles<Theme>(({ palette }) => ({
  surchargesGrid: {
    gap: '8px',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
  },
  surchargeCard: {
    gap: '4px',
    display: 'flex',
    padding: '6px 8px',
    flexDirection: 'column',
    backgroundColor: palette.common.white,
    border: `1px solid ${palette.divider}`,
    borderRadius: '4px',
  },
}));

// Returns loss codes the user may optionally add to a term (isSelectable and not forced-on)
const getSelectableLossCodes = (term: TruckSuiteRateTerm | null): TruckSuiteLossCode[] => {
  if (!term) return [];
  return term.components.flatMap(component =>
    component.lossCodes.filter(lossCode => lossCode.isSelectable && !(lossCode.isSelected && !lossCode.isSelectable))
  );
};

export const Card = ({
  mode,
  product,
  equipmentCost,
  prisingConfig,
  type = 'simple',
  productConfiguration,
  updateProductConfiguration,
}: ProposalProductCardByVendorProps) => {
  const classes = useStyles();
  const isConstructor = mode === ProposalProductCardModes.Constructor;

  const rate = useMemo<TruckSuiteRate | undefined>(() => {
    return product?.aftermarketProduct?.criteria?.[0]?.event?.params?.AFTERMARKET_PRODUCT_VENDOR_API_DATA
      ?.API_RESPONSE_DATA;
  }, [product]);

  prisingConfig.isShowRate = false;

  const logo = vendorsLogoByType[AftermarketProductTypes.TruckSuite];

  const selectedTerm: TruckSuiteRateTerm | null = productConfiguration?.coverage ?? null;
  // Loss codes are tracked via addons (single source of truth) so trash-can removal stays in sync
  const selectedLossCodeIds: number[] = (productConfiguration?.addons ?? []).map((addon: any) =>
    Number(addon.thirdPartyId)
  );

  const calculateCosts = (term: TruckSuiteRateTerm | null, lossCodeIds: number[]) => {
    if (!term) return { cost: 0, retailCost: 0 };

    const selectableLossCodes = getSelectableLossCodes(term);
    const selected = selectableLossCodes.filter(lossCode => lossCodeIds.includes(lossCode.coverageLossCodeId));

    const addonsCost = selected.reduce((sum, lossCode) => sum + lossCode.dealerCost, 0);
    const addonsRetail = selected.reduce((sum, lossCode) => sum + lossCode.suggestedRetailCost, 0);

    return {
      cost: term.dealerCost + addonsCost,
      retailCost: term.suggestedRetailCost + addonsRetail,
    };
  };

  const applyMarkup = (baseCost: number, baseRetailCost: number): number => {
    const markupToUse = productConfiguration?.markup;
    if (!markupToUse || markupToUse.markup === 0) return baseRetailCost;

    if (markupToUse.type === MarkupType.Percentage) {
      return _.round(baseCost * (1 + markupToUse.markup / 100), 2);
    }
    return baseCost + markupToUse.markup;
  };

  // Self-initialize pricing into the (menu) config after a drop so the payment calc gets the cost
  useMemo(() => {
    if (isConstructor || !selectedTerm) return;
    if (productConfiguration?.cost && productConfiguration?.retailCost) return;

    const { cost, retailCost } = calculateCosts(selectedTerm, selectedLossCodeIds);
    if (cost === 0 && retailCost === 0) return;

    updateProductConfiguration({
      cost,
      retailCost: applyMarkup(cost, retailCost),
      markup: productConfiguration?.markup ?? { type: MarkupType.Percentage, markup: 0 },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTerm, productConfiguration]);

  const handleTermChange = (term: TruckSuiteRateTerm) => {
    const { cost, retailCost } = calculateCosts(term, []);
    const markupToUse = productConfiguration?.markup ?? { type: MarkupType.Percentage, markup: 0 };

    updateProductConfiguration({
      code: rate?.code ?? null,
      coverage: term,
      subName: rate?.description ?? null,
      COVERAGE_TERM: term.termOdometer
        ? `${term.termMonths} Months / ${term.termOdometer.toLocaleString()} Miles`
        : `${term.termMonths} Months`,
      addons: [],
      cost,
      retailCost: applyMarkup(cost, retailCost),
      markup: markupToUse,
    });
  };

  const handleLossCodeChange = (lossCode: TruckSuiteLossCode, checked: boolean) => {
    if (!selectedTerm) return;

    let nextAddons: any[] = productConfiguration?.addons ?? [];

    if (checked) {
      nextAddons = [
        ...nextAddons,
        {
          thirdPartyId: String(lossCode.coverageLossCodeId),
          title: lossCode.description,
          cost: lossCode.dealerCost,
          retailCost: lossCode.suggestedRetailCost,
          category: 'Surcharge',
        },
      ];
    } else {
      nextAddons = nextAddons.filter((addon: any) => addon.thirdPartyId !== String(lossCode.coverageLossCodeId));
    }

    const nextLossCodeIds = nextAddons.map((addon: any) => Number(addon.thirdPartyId));
    const { cost, retailCost } = calculateCosts(selectedTerm, nextLossCodeIds);
    const markupToUse = productConfiguration?.markup ?? { type: MarkupType.Percentage, markup: 0 };

    updateProductConfiguration({
      addons: nextAddons,
      cost,
      retailCost: applyMarkup(cost, retailCost),
      markup: markupToUse,
    });
  };

  if (rate === undefined) return null;

  const selectableLossCodes = getSelectableLossCodes(selectedTerm);

  const isPricingEditable = isConstructor || selectedTerm !== null;

  const displayProduct = productConfiguration?.subName
    ? {
        ...product,
        title: `${product.title} ${productConfiguration.subName}`,
      }
    : product;

  return (
    <ProposalCardContainer
      mode={mode}
      type={type}
      logo={logo}
      factsSkipped={[]}
      proposalProduct={displayProduct}
      prisingConfig={prisingConfig}
      equipmentCost={equipmentCost}
      isPricingEditable={isPricingEditable}
      productConfiguration={productConfiguration}
      updateProductConfiguration={updateProductConfiguration}
    >
      <Fields terms={rate.terms} configuration={productConfiguration} handleTermChange={handleTermChange} />

      {!isConstructor && selectedTerm && selectableLossCodes.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            Options
          </Typography>
          <Box className={classes.surchargesGrid}>
            {selectableLossCodes.map(lossCode => {
              const checked = selectedLossCodeIds.includes(lossCode.coverageLossCodeId);
              return (
                <Box key={lossCode.coverageLossCodeId} className={classes.surchargeCard}>
                  <FormControlLabel
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.75rem' }, m: 0 }}
                    label={<ProductPrice prefix={`${lossCode.description}: `} value={lossCode.suggestedRetailCost} />}
                    control={
                      <Checkbox
                        size="small"
                        disableRipple
                        color="primary"
                        checked={checked}
                        onChange={e => handleLossCodeChange(lossCode, e.target.checked)}
                      />
                    }
                  />
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </ProposalCardContainer>
  );
};
